import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { CACHE_TIMES } from 'consts';
import { GetServerSideProps, NextPage } from 'next';
import Head from 'next/head';
import Link from 'next/link';

import api from 'api';
import GuardLoggedIn from 'components/sections/GuardLoggedIn';
import Button from 'components/ui/Button';
import LabelValueRow from 'components/ui/LabelValueRow';
import StaticTextRenderer from 'components/ui/StaticTextRenderer';
import { useUser } from 'contexts/UserContext';
import { AppLayoutProps } from 'types/AppLayoutProps';
import { FooterMenuItem, NavbarMenuItem } from 'types/Menu';
import { MyPage as MyPageType } from 'types/MyPage';
import { SanityUrlBuilder, setCacheHeaders } from 'utils';
import getBalance from 'utils/getBalance';
import getProductsForPickup from 'utils/getProductsForPickup';
import classes from './MyAccount.module.css';

export const getServerSideProps: GetServerSideProps<PageProps> = async ctx => {
  const { sanity } = api();

  const [navbarMenu, menuBanner, footerMenu, page] = await Promise.all([
    sanity.navbarMenu(),
    sanity.menuBanner(),
    sanity.footerMenu(),
    sanity.myPage(),
  ]);

  setCacheHeaders(ctx.res, CACHE_TIMES.MY_PAGES);

  return {
    props: {
      footerMenu,
      menuBanner,
      navbarMenu,
      page,
    },
  };
};

interface PageProps extends AppLayoutProps {
  footerMenu: FooterMenuItem[];
  navbarMenu: NavbarMenuItem[];
  page: MyPageType | null;
}

const Page: NextPage<PageProps> = ({ page }) => {
  const { isLoading: isUserLoading } = useUser();
  const [pickupNotificationCount, setPickupNotificationCount] = useState<number>(0);
  const [balance, setBalance] = useState<string | null | undefined>(undefined);

  const builder = new SanityUrlBuilder(api().sanity.client);

  useEffect(() => {
    let isActive = true;

    getProductsForPickup(0)
      .then(pickupData => {
        if (isActive) {
          setPickupNotificationCount(pickupData?.count ?? 0);
        }
      })
      .catch(() => {
        if (isActive) {
          setPickupNotificationCount(0);
        }
      });

    getBalance()
      .then(balanceData => {
        if (isActive) {
          const balance = balanceData?.balance != null ? String(balanceData.balance) : null;
          setBalance(balance === '0.00' ? '0' : balance);
        }
      })
      .catch(() => {
        if (isActive) {
          setBalance(null);
        }
      });

    return () => {
      isActive = false;
    };
  }, []);

  return (
    <GuardLoggedIn loading={isUserLoading}>
      <Head>
        <title>{page?.title ?? 'Mitt Konto'} - Arkivet Second Hand</title>
      </Head>

      <div className={classes.container}>
        <div className={classes.heroWrapper}>
          {page?.hero && (
            <article className={classes.hero}>
              {page.hero.image && (
                <img
                  src={builder.image(page.hero.image).width(1200).url()}
                  alt=""
                  className={classes.heroImage}
                />
              )}
              <div
                className={clsx(
                  classes.heroContent,
                  page.hero.lightText ? classes.lightText : classes.darkText,
                )}
              >
                <h2 className={classes.heroTitle}>{page.hero.title}</h2>
                <p className={classes.heroText}>
                  {page.hero.content && <StaticTextRenderer text={page.hero.content} />}
                </p>
                <Link href="/boka-tid">
                  <a className={classes.heroButton}>Boka tid</a>
                </Link>
              </div>
            </article>
          )}
        </div>
        <section className={classes.buttonGroup}>
          <article className={classes.summary}>
            <h3 className={classes.summaryTitle}>Mitt saldo</h3>
            <p className={classes.summaryAmount}>
              {balance === undefined ? 'Laddar...' : balance === null ? '-' : `${balance} SEK`}
            </p>

            <Button block={true} className={classes.withdrawButton} href="/mina-sidor/saldo">
              Ta ut pengar
            </Button>

            <Link href="/mina-sidor/transaktioner">
              <a className={classes.transactionHistoryLink}>Transaktionshistorik</a>
            </Link>
          </article>

          <LabelValueRow
            href="/mina-sidor/mina-varor"
            label="Mina varor"
            subtitle="Se vad du har till salu, sålt eller ska hämta."
            notifications={pickupNotificationCount}
            isFirstInSection
            bold
          />

          <LabelValueRow
            href="/boka-tid"
            label="Boka tid"
            subtitle="Hitta en ledig tid hos någon av våra butiker för att lämna in plagg till försäljning."
            bold
          />

          <LabelValueRow href={page?.handInTipsUrl ?? '#'} label="Inlämningstips" bold />

          <LabelValueRow href="/mina-sidor/mina-kop" label="Mina köp" bold />

          <LabelValueRow href="/mina-sidor/profil" label="Min profil" bold />

          <div className={classes.logoutContainer}>
            <a href="/frontend-api/logga-ut">Logga ut</a>
          </div>
        </section>
      </div>
    </GuardLoggedIn>
  );
};

export default Page;
